import { useState, useRef, useEffect, Fragment } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const deg = (v) => `${v.toFixed(4)}\u00b0`;

const DEFAULTS = {
  full_name: "Ada Lovelace",
  dd: "10",
  mm: "12",
  yyyy: "1815",
  birth_time: "22:00",
  birth_time_accuracy: "exact",
  location: "London, United Kingdom",
};

function isValidDate(dd, mm, yyyy) {
  const d = parseInt(dd, 10), m = parseInt(mm, 10), y = parseInt(yyyy, 10);
  if (!dd || !mm || !yyyy) return false;
  if (yyyy.length !== 4) return false;
  if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1 || y > 9999) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export default function Laboratory() {
  const [form, setForm] = useState(DEFAULTS);
  const [meta, setMeta] = useState(null);
  const [chart, setChart] = useState(null);
  const [choices, setChoices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);
  const [view, setView] = useState("lab");

  const [manualOn, setManualOn] = useState(false);
  const [manual, setManual] = useState({ latitude: "", longitude: "", iana_timezone: "" });

  const ddRef = useRef(), mmRef = useRef(), yyyyRef = useRef();

  useEffect(() => {
    axios.get(`${API}/engine/config`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onDatePart = (part, ref, next) => (e) => {
    const raw = e.target.value.replace(/\D/g, "");
    const max = part === "yyyy" ? 4 : 2;
    const val = raw.slice(0, max);
    setForm((f) => ({ ...f, [part]: val }));
    if (val.length === max && next?.current) next.current.focus();
  };

  const dateValid = isValidDate(form.dd, form.mm, form.yyyy);

  const buildPayload = (geonamesId) => {
    const iso = `${form.yyyy.padStart(4, "0")}-${form.mm.padStart(2, "0")}-${form.dd.padStart(2, "0")}`;
    const p = {
      full_name: form.full_name,
      birth_date: iso,
      birth_time: form.birth_time_accuracy === "unknown" ? null : form.birth_time,
      birth_time_accuracy: form.birth_time_accuracy,
    };
    if (manualOn) {
      p.manual_override = {
        latitude: parseFloat(manual.latitude),
        longitude: parseFloat(manual.longitude),
        iana_timezone: manual.iana_timezone,
        canonical_city: "Lab manual entry",
      };
    } else {
      p.location = form.location;
      if (geonamesId) p.geonames_id = geonamesId;
    }
    return p;
  };

  const compute = async (geonamesId) => {
    if (!dateValid) {
      setChart(null);
      setChoices(null);
      setError("Please enter a valid date (DD / MM / YYYY).");
      return;
    }
    setLoading(true);
    setError(null);
    setChart(null);
    if (!geonamesId) setChoices(null);
    try {
      const r = await axios.post(`${API}/diagnostic/run`, buildPayload(geonamesId));
      setChart(r.data);
      setChoices(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.choices) {
        setChoices(detail.choices);
        setError(null);
      } else {
        setError(typeof detail === "string" ? detail : detail?.message || e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const exportRecord = () => {
    if (!chart) return;
    const record = {
      export_version: "star-haven-record-1",
      exported_at: new Date().toISOString(),
      engine: {
        calibration_status: chart.diagnostic?.calibration_status,
        calibration_version: chart.diagnostic?.calibration_version,
        qualification: chart.diagnostic?.qualification,
      },
      birth_input: {
        full_name: form.full_name,
        birth_date: form.birth_date,
        birth_time: form.birth_time,
        birth_time_accuracy: form.birth_time_accuracy,
        location: form.location,
      },
      result: chart,
    };
    const slug = (form.full_name || "chart").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    const fname = `star-haven_${slug || "chart"}_${form.birth_date || "record"}.json`;
    const blob = new Blob([JSON.stringify(record, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[#0a0a14] text-slate-200 selection:bg-indigo-500/40">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(1px 1px at 20% 30%, #fff, transparent), radial-gradient(1px 1px at 70% 60%, #cbd5ff, transparent), radial-gradient(1.5px 1.5px at 40% 80%, #fff, transparent), radial-gradient(1px 1px at 85% 20%, #a5b4fc, transparent)",
          backgroundSize: "400px 400px",
        }}
      />
      <div className="relative max-w-6xl mx-auto px-6 py-12">
        <header className="mb-10">
          <p className="uppercase tracking-[0.35em] text-[11px] text-indigo-400/80">
            Star Haven &middot; Natal Crystal Engine
          </p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-light text-white">Ephemeris Laboratory</h1>
          <p className="mt-3 text-sm text-slate-400 max-w-2xl">
            Internal inspection + diagnosis harness. Type a birth date directly, enter one
            city &amp; country field, and compute a raw Swiss-Ephemeris natal object plus the
            ungated regulation × field diagnosis plus committed Threshold qualification
            (single global cutoff ≥ 1.0, provisional) and chart-conditioned Anchor.
          </p>

          <div className="mt-6 inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="view-toggle">
            <button
              data-testid="view-lab"
              onClick={() => setView("lab")}
              className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${
                view === "lab" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Ephemeris
            </button>
            <button
              data-testid="view-catalog"
              onClick={() => setView("catalog")}
              className={`px-4 py-1.5 text-xs uppercase tracking-widest rounded-full transition-colors ${
                view === "catalog" ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              Stone Catalogue
            </button>
          </div>
        </header>

        {view === "catalog" && <CataloguePanel />}

        {view === "lab" && (
        <div className="grid lg:grid-cols-[400px_1fr] gap-8">
          {/* form */}
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl h-fit">
            <div className="space-y-4">
              <Input label="Full name" value={form.full_name} onChange={update("full_name")} testId="input-full-name" />

              {/* Typed date DD / MM / YYYY */}
              <div>
                <label className="block text-xs uppercase tracking-widest text-indigo-300/70 mb-1.5">
                  Birth date (DD / MM / YYYY)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={ddRef} data-testid="input-day" inputMode="numeric" placeholder="DD"
                    value={form.dd} onChange={onDatePart("dd", ddRef, mmRef)}
                    className="w-14 text-center rounded-lg bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-indigo-400 outline-none"
                  />
                  <span className="text-slate-500">/</span>
                  <input
                    ref={mmRef} data-testid="input-month" inputMode="numeric" placeholder="MM"
                    value={form.mm} onChange={onDatePart("mm", mmRef, yyyyRef)}
                    className="w-14 text-center rounded-lg bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-indigo-400 outline-none"
                  />
                  <span className="text-slate-500">/</span>
                  <input
                    ref={yyyyRef} data-testid="input-year" inputMode="numeric" placeholder="YYYY"
                    value={form.yyyy} onChange={onDatePart("yyyy", yyyyRef, null)}
                    className="w-20 text-center rounded-lg bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-indigo-400 outline-none"
                  />
                  <input
                    type="date" data-testid="input-date-picker" aria-label="calendar picker (optional)"
                    onChange={(e) => {
                      const [y, m, d] = e.target.value.split("-");
                      if (y) setForm((f) => ({ ...f, dd: d, mm: m, yyyy: y }));
                    }}
                    className="ml-1 w-9 rounded-lg bg-black/40 border border-white/10 text-transparent [color-scheme:dark] cursor-pointer"
                    title="Optional calendar picker"
                  />
                </div>
                {!dateValid && (form.dd || form.mm || form.yyyy) && (
                  <p data-testid="date-error" className="mt-1 text-xs text-rose-400">Not a valid date.</p>
                )}
              </div>

              <Input
                label="Birth time"
                type="time"
                value={form.birth_time}
                onChange={update("birth_time")}
                disabled={form.birth_time_accuracy === "unknown"}
                testId="input-birth-time"
              />
              <div>
                <label className="block text-xs uppercase tracking-widest text-indigo-300/70 mb-1.5">Time accuracy</label>
                <select
                  data-testid="select-accuracy" value={form.birth_time_accuracy} onChange={update("birth_time_accuracy")}
                  className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-indigo-400 outline-none"
                >
                  <option value="exact">exact</option>
                  <option value="approximate">approximate</option>
                  <option value="unknown">unknown</option>
                </select>
              </div>

              <Input
                label="City & country of birth"
                value={form.location}
                onChange={(e) => { setChoices(null); update("location")(e); }}
                placeholder="e.g. Fort-de-France, Martinique"
                disabled={manualOn}
                testId="input-location"
              />

              {/* choices */}
              {choices && (
                <div data-testid="location-choices" className="rounded-xl border border-indigo-400/30 bg-indigo-500/10 p-3 space-y-2">
                  <p className="text-xs text-indigo-200">Several matches — choose one:</p>
                  {choices.map((c) => (
                    <button
                      key={c.geonames_id} data-testid={`choice-${c.geonames_id}`}
                      onClick={() => compute(c.geonames_id)}
                      className="w-full text-left text-sm rounded-lg bg-black/30 hover:bg-black/50 border border-white/10 px-3 py-2 transition-colors"
                    >
                      {c.label}
                      <span className="text-slate-500"> &middot; {c.iana_timezone}
                        {c.population ? ` \u00b7 pop ${c.population.toLocaleString()}` : ""}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* lab-only manual override */}
              <div className="rounded-xl border border-white/10 bg-black/20 p-3">
                <label className="flex items-center gap-2 text-xs text-amber-300/90 cursor-pointer">
                  <input type="checkbox" data-testid="manual-toggle" checked={manualOn}
                    onChange={(e) => setManualOn(e.target.checked)} />
                  Manual coordinate override (laboratory only)
                </label>
                {manualOn && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <input data-testid="manual-lat" placeholder="latitude" value={manual.latitude}
                      onChange={(e) => setManual({ ...manual, latitude: e.target.value })}
                      className="rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-indigo-400" />
                    <input data-testid="manual-lon" placeholder="longitude" value={manual.longitude}
                      onChange={(e) => setManual({ ...manual, longitude: e.target.value })}
                      className="rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-indigo-400" />
                    <input data-testid="manual-tz" placeholder="IANA timezone e.g. Asia/Kolkata" value={manual.iana_timezone}
                      onChange={(e) => setManual({ ...manual, iana_timezone: e.target.value })}
                      className="col-span-2 rounded-lg bg-black/40 border border-white/10 px-2 py-1.5 text-xs font-mono outline-none focus:border-indigo-400" />
                  </div>
                )}
              </div>

              <button
                data-testid="compute-button" onClick={() => compute()} disabled={loading}
                className="w-full mt-2 rounded-full bg-indigo-500 hover:bg-indigo-400 transition-colors text-white py-2.5 text-sm font-medium disabled:opacity-50"
              >
                {loading ? "Calculating\u2026" : "Compute Natal Chart"}
              </button>
              {meta && (
                <p className="text-[11px] text-slate-500 pt-2">
                  engine {meta.engine_versions.astrology_calculation_version} &middot;{" "}
                  {meta.default_config.house_system} &middot; {meta.default_config.zodiac_mode}
                </p>
              )}
            </div>
          </div>

          {/* results */}
          <div>
            {error && (
              <div data-testid="error-box" className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-rose-200 text-sm">
                {error}
              </div>
            )}

            {!chart && !error && !choices && (
              <div className="rounded-2xl border border-dashed border-white/10 p-16 text-center text-slate-500">
                Enter birth data and compute to inspect the raw chart object.
              </div>
            )}

            {chart && (
              <div className="space-y-6" data-testid="chart-result">
                <Panel title="Resolved Location & Time">
                  <div className="grid sm:grid-cols-2 gap-x-8">
                    <Row label="You typed">{chart.resolved_location.original_location_text}</Row>
                    <Row label="Canonical" testId="canonical-city">
                      {chart.resolved_location.canonical_city}, {chart.resolved_location.country_territory}
                    </Row>
                    <Row label="Country code">{chart.resolved_location.country_code || "\u2014"}</Row>
                    <Row label="Latitude" testId="lat">{chart.resolved_location.latitude.toFixed(5)}</Row>
                    <Row label="Longitude">{chart.resolved_location.longitude.toFixed(5)}</Row>
                    <Row label="IANA timezone" testId="tz">{chart.resolved_location.iana_timezone}</Row>
                    <Row label="TZ method">{chart.resolved_location.timezone_resolution_method}</Row>
                    <Row label="Dataset">{chart.resolved_location.dataset_version}</Row>
                    <Row label="UTC offset" testId="utc-offset">{chart.resolved_time.utc_offset_hours}h</Row>
                    <Row label="Local datetime">{chart.resolved_time.local_datetime}</Row>
                    <Row label="UTC datetime" testId="utc-dt">{chart.resolved_time.utc_datetime}</Row>
                    <Row label="Angles reliable">{String(chart.resolved_time.angles_reliable)}</Row>
                    <Row label="Julian Day (UT)">{chart.julian_day_ut.toFixed(6)}</Row>
                  </div>
                </Panel>

                {chart.warnings.length > 0 && (
                  <div data-testid="warnings" className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-amber-200/90 text-sm space-y-1">
                    {chart.warnings.map((w, i) => (<p key={i}>&#9888; {w}</p>))}
                  </div>
                )}

                <Panel title="Points">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="points-table">
                      <thead>
                        <tr className="text-indigo-300/70 text-left text-xs uppercase tracking-wider">
                          <th className="py-2 pr-4">Body</th><th className="py-2 pr-4">Sign</th>
                          <th className="py-2 pr-4">Deg</th><th className="py-2 pr-4">House</th>
                          <th className="py-2 pr-4">Longitude</th><th className="py-2 pr-4">Speed</th><th className="py-2">R</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {chart.points.map((p) => (
                          <tr key={p.name} className="border-t border-white/5" data-testid={`point-${p.name}`}>
                            <td className="py-1.5 pr-4 capitalize text-slate-100">{p.name.replace("_", " ")}</td>
                            <td className="py-1.5 pr-4">{p.sign}</td>
                            <td className="py-1.5 pr-4">{deg(p.degree_in_sign)}</td>
                            <td className="py-1.5 pr-4">{p.house ?? "\u2014"}</td>
                            <td className="py-1.5 pr-4 text-slate-400">{p.longitude.toFixed(4)}</td>
                            <td className="py-1.5 pr-4 text-slate-400">{p.speed_longitude.toFixed(4)}</td>
                            <td className="py-1.5">{p.retrograde ? <span className="text-rose-400">R</span> : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>

                {chart.ascendant && (
                  <Panel title="Angles">
                    <div className="grid sm:grid-cols-2 gap-x-8">
                      <Row label="Ascendant" testId="asc">{chart.ascendant.sign} {deg(chart.ascendant.degree_in_sign)}</Row>
                      <Row label="Midheaven">{chart.midheaven.sign} {deg(chart.midheaven.degree_in_sign)}</Row>
                      <Row label="Descendant">{chart.descendant.sign} {deg(chart.descendant.degree_in_sign)}</Row>
                      <Row label="Imum Coeli">{chart.imum_coeli.sign} {deg(chart.imum_coeli.degree_in_sign)}</Row>
                    </div>
                  </Panel>
                )}

                {chart.diagnostic && <DiagnosticsPanel diag={chart.diagnostic} />}

                <div className="flex items-center gap-4">
                  <button data-testid="export-record-button" onClick={exportRecord}
                    className="rounded-full bg-indigo-500/90 hover:bg-indigo-400 text-white text-xs font-medium px-4 py-2 transition-colors">
                    Export full record (JSON)
                  </button>
                  <button data-testid="toggle-raw" onClick={() => setShowRaw(!showRaw)}
                    className="text-xs text-indigo-400 hover:text-indigo-300">
                    {showRaw ? "Hide" : "Show"} raw JSON object
                  </button>
                </div>
                {showRaw && (
                  <pre className="mt-3 rounded-xl bg-black/50 border border-white/10 p-4 text-[11px] text-slate-300 overflow-x-auto max-h-96">
                    {JSON.stringify(chart, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

function Input({ label, testId, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-indigo-300/70 mb-1.5">{label}</label>
      <input data-testid={testId} {...props}
        className="w-full rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-indigo-400 outline-none disabled:opacity-40" />
    </div>
  );
}

function Row({ label, children, testId }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-white/5 text-sm">
      <span className="text-indigo-300/70">{label}</span>
      <span className="text-right text-slate-100 font-mono" data-testid={testId}>{children}</span>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-xl">
      <h2 className="text-xs uppercase tracking-[0.25em] text-indigo-400/80 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function DiagnosticsPanel({ diag }) {
  const [open, setOpen] = useState(null);
  const factsById = Object.fromEntries(diag.facts.map((f) => [f.fact_id, f]));

  return (
    <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-6 backdrop-blur-xl" data-testid="diagnostics-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xs uppercase tracking-[0.25em] text-fuchsia-300/80">Diagnosis · Regulation × Field</h2>
        <span className="text-[10px] uppercase tracking-widest text-amber-300/80 border border-amber-400/30 rounded-full px-2 py-0.5">
          {diag.calibration_status} · qualification {diag.qualification}
        </span>
      </div>
      <p className="text-[11px] text-slate-400 mb-4">{diag.calibration_version}</p>

      {diag.cells.length === 0 ? (
        <div data-testid="no-cells" className="rounded-lg border border-dashed border-white/10 p-6 text-center text-slate-400 text-sm">
          No regulation × field cell received evidence. That is a valid result — the chart is quiet on this axis.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="cells-table">
          <thead>
            <tr className="text-fuchsia-300/70 text-left text-xs uppercase tracking-wider">
              <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Regulation × Field</th>
              <th className="py-2 pr-3 text-right">Adjusted</th><th className="py-2 pr-3 text-right">Raw</th>
              <th className="py-2 pr-3 text-center">Indep.</th><th className="py-2">Configs</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {diag.cells.map((c, i) => (
              <Fragment key={`${c.regulation_mode}-${c.field}`}>
                <tr data-testid={`cell-${i}`}
                  onClick={() => setOpen(open === i ? null : i)}
                  className="border-t border-white/5 cursor-pointer hover:bg-white/5">
                  <td className="py-2 pr-3 text-slate-500">{i + 1}</td>
                  <td className="py-2 pr-3 text-slate-100">
                    <span className={c.regulation_mode === "Compression" ? "text-sky-300" : "text-violet-300"}>{c.regulation_mode}</span>
                    <span className="text-slate-500"> × </span>{c.field}
                  </td>
                  <td className="py-2 pr-3 text-right text-fuchsia-200">{c.adjusted_score.toFixed(3)}</td>
                  <td className="py-2 pr-3 text-right text-slate-400">{c.raw_score.toFixed(3)}</td>
                  <td className="py-2 pr-3 text-center text-slate-300">{c.independent_evidence_count}</td>
                  <td className="py-2 text-slate-400">{c.contributing_configuration_ids.join(", ") || "—"}</td>
                </tr>
                {open === i && (
                  <tr data-testid={`cell-facts-${i}`}>
                    <td colSpan={6} className="py-2 pl-6 pr-3 bg-black/30">
                      {c.fact_ids.map((fid) => {
                        const f = factsById[fid];
                        return (
                          <div key={fid} className="text-[12px] text-slate-300 py-1 border-b border-white/5 last:border-0">
                            <span className="text-fuchsia-300">{f.fact_id}</span>{" "}
                            {f.planet_a} <span className="text-slate-500">({f.regulation_mode})</span>{" "}
                            {f.aspect_type} {f.planet_b} <span className="text-slate-300">({f.field})</span>{" "}
                            · orb {f.orb}°/{f.max_orb} · orb_strength {f.orb_strength}
                            {" "}· centrality {f.centrality_tier}×{f.centrality_multiplier} (via {f.centrality_from})
                            {" "}· raw {f.raw_weight} · decay #{f.decay_rank}×{f.decay_multiplier} → <span className="text-fuchsia-200">adj {f.adjusted_weight}</span>
                            {" "}· cfg {f.configuration_id || "independent"}
                            {f.visibility_gates && f.visibility_gates.length > 0 && (
                              <span className="text-emerald-300"> · gate: {f.visibility_gates.join(", ")}</span>
                            )}
                          </div>
                        );
                      })}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-1">Configurations</p>
          <div className="text-xs font-mono text-slate-400 space-y-0.5" data-testid="config-list">
            {diag.configurations.length === 0 && <span>none</span>}
            {diag.configurations.map((cf) => (
              <div key={cf.configuration_id}>
                <span className="text-slate-200">{cf.configuration_id}</span> {cf.topology} · {cf.members.join(", ")} · span {cf.span}°
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-1">Notes</p>
          <div className="text-[11px] text-slate-300 space-y-1">
            {diag.notes.map((n, i) => <p key={i}>{n}</p>)}
          </div>
        </div>
      </div>

      {diag.identity_vitality_candidates && diag.identity_vitality_candidates.length > 0 && (
        <div className="mt-4 rounded-lg border border-amber-400/20 bg-amber-500/[0.04] p-4" data-testid="ivc-panel">
          <p className="text-[10px] uppercase tracking-widest text-amber-300/80 mb-2">
            Identity / Vitality candidates · ungated Sun facts (unscored, retained for review)
          </p>
          <div className="text-[12px] font-mono text-slate-300 space-y-1">
            {diag.identity_vitality_candidates.map((v, i) => (
              <div key={i} data-testid={`ivc-${i}`} className="py-1 border-b border-white/5 last:border-0">
                <span className="text-amber-300">{v.atomic_condition_id}</span>{" "}
                {v.regulation_supplier} <span className="text-slate-500">({v.regulation_mode})</span>{" "}
                {v.aspect_type} {v.counterpart} · orb {v.orb}°/{v.max_orb}
                {" "}· <span className="text-slate-400">{v.annotation}</span>
                {" "}· <span className="text-slate-300">{v.reason}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {diag.regulation_pole_pairs && diag.regulation_pole_pairs.length > 0 && (
        <div className="mt-4 rounded-lg border border-sky-400/20 bg-sky-500/[0.04] p-4" data-testid="pole-pairs-panel">
          <p className="text-[10px] uppercase tracking-widest text-sky-300/80 mb-2">
            Dynamic supplier pairs · two regulation poles, no field (propose no cell)
          </p>
          <div className="text-[12px] font-mono text-slate-300 space-y-1">
            {diag.regulation_pole_pairs.map((p, i) => (
              <div key={i} data-testid={`pole-pair-${i}`} className="py-1 border-b border-white/5 last:border-0">
                <span className="text-sky-300">{p.atomic_condition_id}</span>{" "}
                {p.supplier_a} <span className="text-slate-300">({p.pole_a})</span>{" "}
                {p.aspect_type} {p.supplier_b} <span className="text-slate-300">({p.pole_b})</span>
                {" "}· orb {p.orb}°/{p.max_orb} · cfg {p.configuration_id || "none"}
                {p.field_suppliers_in_configuration.length > 0 && (
                  <span className="text-amber-300"> · regulation contest with {p.field_suppliers_in_configuration.join(", ")}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {diag.qualification_report && (
        <div className="mt-4 rounded-lg border border-emerald-400/20 bg-emerald-500/[0.04] p-4" data-testid="qualification-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300/80">
              Qualification · sensitivity (candidate thresholds)
            </p>
            <span className="text-[10px] uppercase tracking-widest text-amber-300/80 border border-amber-400/30 rounded-full px-2 py-0.5" data-testid="qual-mode">
              {diag.qualification_report.mode} · selection {diag.qualification_report.stone_selection}
            </span>
          </div>

          {/* threshold sweep */}
          <p className="text-[10px] uppercase tracking-widest text-emerald-300/60 mb-1">Cells qualifying by candidate threshold</p>
          <div className="text-[12px] font-mono text-slate-300 space-y-0.5 mb-3" data-testid="qual-sweep">
            {diag.qualification_report.candidate_thresholds.map((t) => {
              const hits = diag.qualification_report.qualifying_by_threshold[String(t)] || [];
              return (
                <div key={t} className="py-0.5 border-b border-white/5 last:border-0" data-testid={`sweep-${t}`}>
                  <span className="text-emerald-300">t ≥ {t}</span>{" "}
                  <span className="text-slate-500">·</span>{" "}
                  {hits.length ? hits.join(", ") : <span className="text-slate-600">none</span>}
                </div>
              );
            })}
          </div>

          {/* per-cell eligible stones */}
          <p className="text-[10px] uppercase tracking-widest text-emerald-300/60 mb-1">Eligible + scoring-eligible stones per cell (not selected)</p>
          <div className="space-y-1" data-testid="qual-cells">
            {diag.qualification_report.cells.map((c, i) => (
              <div key={i} data-testid={`qual-cell-${i}`} className="text-[12px] font-mono py-1 border-b border-white/5 last:border-0">
                <span className="text-slate-100">{c.regulation_mode} × {c.field}</span>
                <span className="text-slate-500"> · score {c.adjusted_score.toFixed(3)} · indep {c.independent_evidence_count}</span>
                <span className="text-emerald-300"> · qualifies_at {c.qualifies_at.length ? c.qualifies_at.join(", ") : "—"}</span>
                <div className="pl-3 mt-0.5">
                  {c.eligible_stones.length === 0 ? (
                    <span className="text-slate-600">no authored primary-cell stone</span>
                  ) : (
                    c.eligible_stones.map((s) => (
                      <span
                        key={s.id}
                        data-testid={`qual-stone-${s.id}`}
                        className={`inline-block mr-2 mb-1 rounded-full border px-2 py-0.5 ${
                          s.would_be_available
                            ? "border-emerald-400/30 text-emerald-200"
                            : "border-slate-500/30 text-slate-400"
                        }`}
                        title={`status ${s.affinity_status} · engine ${s.engine_enabled} · scoring ${s.scoring_eligible} · threshold_eligible ${s.threshold_eligible} · permitted ${s.threshold_permitted}`}
                      >
                        {s.name}{s.would_be_available ? " ✓" : ""}
                      </span>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {diag.threshold_selection && (
        <div className="mt-4 rounded-lg border border-amber-400/25 bg-amber-500/[0.05] p-4" data-testid="threshold-selection-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-widest text-amber-300/90">
              Threshold · committed selection
            </p>
            <span className="text-[10px] uppercase tracking-widest text-amber-200 border border-amber-400/40 rounded-full px-2 py-0.5" data-testid="threshold-cutoff-badge">
              cutoff ≥ {diag.threshold_selection.committed_threshold} · {diag.threshold_selection.scope} · provisional
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mb-3" data-testid="threshold-summary">
            {diag.threshold_selection.qualified_cell_count} cell(s) qualified ·
            {" "}{diag.threshold_selection.selected_stone_count} Threshold stone(s) selected.
            A cell qualifies when adjusted_score ≥ {diag.threshold_selection.committed_threshold}.
            Nothing is manufactured — a qualified cell with no available stone selects none.
          </p>
          <div className="space-y-1" data-testid="threshold-cells">
            {diag.threshold_selection.cells.map((c, i) => (
              <div key={i} data-testid={`threshold-cell-${i}`} className={`text-[12px] font-mono py-1 border-b border-white/5 last:border-0 ${c.qualified ? "" : "opacity-45"}`}>
                <span className="text-slate-100">{c.regulation_mode} × {c.field}</span>
                <span className="text-slate-500"> · score {c.adjusted_score.toFixed(3)} · indep {c.independent_evidence_count}</span>
                {c.qualified
                  ? <span className="text-amber-300" data-testid={`threshold-qualified-${i}`}> · QUALIFIED</span>
                  : <span className="text-slate-600"> · below cutoff</span>}
                {c.qualified && (
                  <div className="pl-3 mt-0.5">
                    {c.selected_stones.length === 0 ? (
                      <span className="text-slate-600">qualified · no available Threshold stone (none selected)</span>
                    ) : (
                      c.selected_stones.map((s) => (
                        <span key={s.id} data-testid={`threshold-stone-${s.id}`}
                          className="inline-block mr-2 mb-1 rounded-full border border-amber-400/40 text-amber-100 px-2 py-0.5"
                          title={`status ${s.affinity_status} · engine ${s.engine_enabled} · scoring ${s.scoring_eligible} · threshold_eligible ${s.threshold_eligible} · permitted ${s.threshold_permitted}`}>
                          {s.name} ✓
                        </span>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {diag.anchor_reading && (
        <div className="mt-4 rounded-lg border border-sky-400/20 bg-sky-500/[0.04] p-4" data-testid="anchor-panel">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] uppercase tracking-widest text-sky-300/80">
              Anchor pool · {diag.anchor_reading.total_anchor_stones} stones · {diag.anchor_reading.dimensions.length} dimensions
            </p>
            <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-400/40 rounded-full px-2 py-0.5" data-testid="anchor-unconditioned-badge">
              ⚠ unconditioned by chart · not a real anchor result
            </span>
          </div>
          <p className="text-[11px] text-slate-500 mb-3" data-testid="anchor-disclaimer">
            Catalog-driven pool of Anchor-eligible stones (engine-enabled, anchor-permitted,
            scoring-eligible integration affinity), grouped by dimension. Identical for every
            chart until the chart→integration mapping is authored. Threshold selection is OFF.
          </p>
          <div className="space-y-1" data-testid="anchor-dimensions">
            {diag.anchor_reading.dimensions.map((g) => (
              <div key={g.dimension} data-testid={`anchor-dim-${g.dimension}`} className="text-[12px] font-mono py-1 border-b border-white/5 last:border-0">
                <span className="text-sky-200">{g.dimension}</span>
                <span className="text-slate-500"> · depth {g.depth}</span>
                <div className="pl-3 mt-0.5">
                  {g.stones.map((s) => (
                    <span key={s.id} className="inline-block mr-2 mb-1 rounded-full border border-sky-400/25 text-slate-300 px-2 py-0.5"
                      title={`${s.affinity_status} · ${s.origin}`}>
                      {s.name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {diag.integration_profile && (
        <div className="mt-4 rounded-lg border border-violet-400/20 bg-violet-500/[0.04] p-4" data-testid="integration-profile-panel">
          <p className="text-[10px] uppercase tracking-widest text-violet-300/80 mb-2">
            Integration need profile (Sprint 5 · provisional)
          </p>
          <div className="text-[12px] font-mono mb-2" data-testid="need-weights">
            {Object.keys(diag.integration_profile.need_weights).length === 0
              ? <span className="text-slate-500">no rules fired</span>
              : Object.entries(diag.integration_profile.need_weights).map(([d, w]) => (
                  <span key={d} className="mr-3 text-violet-200">{d} <span className="text-slate-400">{w}</span></span>
                ))}
          </div>
          <p className="text-[10px] uppercase tracking-widest text-violet-300/60 mb-1">Fired rules</p>
          <ul className="text-[12px] text-slate-400 list-disc list-inside mb-2" data-testid="fired-rules">
            {diag.integration_profile.fired_rules.map((r) => (
              <li key={r.id}>#{r.id} {r.name} <span className="text-slate-600 font-mono">{Object.entries(r.weights).map(([d, w]) => `${d} ${w}`).join(" · ")}</span></li>
            ))}
          </ul>
          {diag.integration_profile.skipped_rules.length > 0 && (
            <div data-testid="skipped-rules" className="text-[11px] text-amber-300/80">
              Skipped (need exact birth time): {diag.integration_profile.skipped_rules.map((r) => `#${r.id}`).join(", ")}
            </div>
          )}
        </div>
      )}

      {diag.conditioned_anchor && (
        <div className="mt-4 rounded-lg border border-emerald-400/25 bg-emerald-500/[0.05] p-4" data-testid="conditioned-anchor-panel">
          <p className="text-[10px] uppercase tracking-widest text-emerald-300/80 mb-2">
            Anchor · chart-conditioned (ranked by need · need&gt;0 cutoff applied · {diag.conditioned_anchor.dimensions.length} dimensions)
          </p>
          <div className="space-y-1" data-testid="conditioned-anchor-dims">
            {diag.conditioned_anchor.dimensions.map((g) => (
              <div key={g.dimension} data-testid={`cond-dim-${g.dimension}`} className="text-[12px] font-mono py-1 border-b border-white/5 last:border-0">
                <span className="text-emerald-200">{g.dimension}</span>
                <span className="text-slate-400"> · need {g.need_weight}</span>
                <span className="text-slate-600"> · {g.stones.length} stones</span>
                <div className="pl-3 mt-0.5">
                  {g.stones.map((s) => (
                    <span key={s.id} data-testid={`cond-stone-${s.id}`} className="inline-block mr-2 mb-1 rounded-full border border-emerald-400/25 text-slate-300 px-2 py-0.5" title={`${s.affinity_status} · ${s.origin}`}>{s.name}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


const REASON_LABELS = {
  insufficient_engine_evidence: "Insufficient engine evidence",
  family_subrecord_required: "Family sub-record required",
  provisional_only: "Provisional only",
  disputed_provenance: "Disputed provenance",
  no_current_selection_pathway: "No current selection pathway",
};
const REASON_DELIBERATE = new Set([
  "family_subrecord_required",
  "provisional_only",
  "disputed_provenance",
  "no_current_selection_pathway",
]);

function CataloguePanel() {
  const [summary, setSummary] = useState(null);
  const [stones, setStones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const [engineFilter, setEngineFilter] = useState("all"); // all | enabled | disabled
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      axios.get(`${API}/catalog/summary`),
      axios.get(`${API}/catalog/stones`),
    ])
      .then(([s, st]) => {
        if (!alive) return;
        setSummary(s.data);
        setStones(st.data.stones || []);
      })
      .catch((e) => alive && setErr(e?.response?.data?.detail || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="text-slate-500 py-16 text-center" data-testid="catalog-loading">Loading catalogue…</div>;
  if (err) return <div data-testid="catalog-error" className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-4 text-rose-200 text-sm">{String(err)}</div>;

  const term = q.trim().toLowerCase();
  const filtered = stones.filter((s) => {
    if (engineFilter === "enabled" && !s.engine_enabled) return false;
    if (engineFilter === "disabled" && s.engine_enabled) return false;
    if (term && !(`${s.name} ${s.id}`.toLowerCase().includes(term))) return false;
    return true;
  });

  return (
    <div className="space-y-6" data-testid="catalog-panel">
      {/* summary */}
      <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-6 backdrop-blur-xl" data-testid="catalog-summary">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs uppercase tracking-[0.25em] text-fuchsia-300/80">Imported Catalogue · {summary.dataset}</h2>
          <span className="text-[10px] uppercase tracking-widest text-amber-300/80 border border-amber-400/30 rounded-full px-2 py-0.5">
            import + validate only · no scoring
          </span>
        </div>
        <div className="grid sm:grid-cols-4 gap-4 mb-5">
          <Stat label="Total stones" value={`${summary.total_stones} / ${summary.expected_stones}`} testId="stat-total" />
          <Stat label="Catalog active" value={`${summary.catalog_active.true} true / ${summary.catalog_active.false} false`} />
          <Stat label="Engine enabled" value={`${summary.engine_enabled.true} on / ${summary.engine_enabled.false} off`} testId="stat-engine" />
          <Stat label="Scoring-eligible affinities" value={`${summary.scoring_eligible_affinities.true} yes / ${summary.scoring_eligible_affinities.false} no`} />
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-2">Disabled by reason</p>
            <div className="space-y-1 text-sm font-mono" data-testid="disabled-by-reason">
              {Object.entries(summary.disabled_by_reason).map(([r, n]) => (
                <div key={r} className="flex justify-between gap-4 border-b border-white/5 py-1">
                  <span className={REASON_DELIBERATE.has(r) ? "text-sky-300" : "text-rose-300"}>
                    {REASON_LABELS[r] || r}
                    <span className="text-slate-600"> · {REASON_DELIBERATE.has(r) ? "deliberate" : "evidence gap"}</span>
                  </span>
                  <span className="text-slate-200">{n}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-2">Authored primary cells (regulation × field)</p>
            <div className="space-y-1 text-sm font-mono" data-testid="cell-coverage">
              {Object.keys(summary.cell_coverage).length === 0 && <span className="text-slate-500">none</span>}
              {Object.entries(summary.cell_coverage).map(([cell, cov]) => (
                <div key={cell} className="border-b border-white/5 py-1">
                  <span className="text-slate-200">{cell.replace("x", " × ")}</span>
                  <span className="text-slate-500"> · authored {cov.authored} · scoring-eligible {cov.scoring_eligible} · threshold {cov.threshold_eligible}</span>
                  <span className="text-slate-600"> · {cov.stones.join(", ")}</span>
                </div>
              ))}
            </div>
            <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mt-4 mb-2">Calibration hypotheses</p>
            <div className="text-sm font-mono text-slate-300" data-testid="hypothesis-counts">
              {Object.entries(summary.calibration_hypotheses_by_status).map(([st, n]) => (
                <span key={st} className="mr-4">{st}: <span className="text-slate-100">{n}</span></span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-4 text-[11px]">
          <span className={summary.errors.length ? "text-rose-300" : "text-emerald-300"} data-testid="validation-status">
            {summary.errors.length ? `${summary.errors.length} validation error(s)` : "✓ no validation errors"}
          </span>
          <span className={summary.duplicate_ids.length ? "text-rose-300" : "text-emerald-300"}>
            {summary.duplicate_ids.length ? `dupes: ${summary.duplicate_ids.join(", ")}` : "✓ no duplicate ids"}
          </span>
          <span className="text-emerald-300">✓ no stone engine-enabled by class alone</span>
        </div>
      </div>

      {/* controls */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          data-testid="catalog-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search stone name or id…"
          className="flex-1 min-w-[220px] rounded-lg bg-black/40 border border-white/10 px-3 py-2 text-sm focus:border-indigo-400 outline-none"
        />
        <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-1" data-testid="engine-filter">
          {["all", "enabled", "disabled"].map((f) => (
            <button
              key={f} data-testid={`filter-${f}`} onClick={() => setEngineFilter(f)}
              className={`px-3 py-1 text-xs rounded-md capitalize transition-colors ${
                engineFilter === f ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-500" data-testid="catalog-count">{filtered.length} shown</span>
      </div>

      {/* stone list */}
      <div className="space-y-2" data-testid="catalog-list">
        {filtered.map((s) => (
          <div key={s.id} className="rounded-xl border border-white/10 bg-white/[0.03]" data-testid={`stone-${s.id}`}>
            <button
              onClick={() => setOpen(open === s.id ? null : s.id)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
            >
              <span className="text-slate-500 font-mono text-xs w-8">{s.number}</span>
              <span className="text-slate-100 flex-1">{s.name}
                <span className="text-slate-600 text-xs font-mono"> · {s.star_haven_class}</span>
              </span>
              {s.engine_enabled ? (
                <span className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-400/30 rounded-full px-2 py-0.5" data-testid={`badge-${s.id}`}>
                  engine on
                </span>
              ) : (
                <span
                  data-testid={`badge-${s.id}`}
                  className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 border ${
                    REASON_DELIBERATE.has(s.engine_disable_reason)
                      ? "text-sky-300 border-sky-400/30"
                      : "text-rose-300 border-rose-400/30"
                  }`}
                  title={REASON_LABELS[s.engine_disable_reason]}
                >
                  {REASON_LABELS[s.engine_disable_reason] || "disabled"}
                </span>
              )}
            </button>
            {open === s.id && (
              <div className="px-4 pb-4 pt-1 border-t border-white/5 text-sm space-y-3" data-testid={`stone-detail-${s.id}`}>
                <p className="text-slate-400 italic">{s.description}</p>

                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono text-slate-400">
                  <span>catalog_active: <span className="text-slate-200">{String(s.catalog_active)}</span></span>
                  <span>engine_enabled: <span className="text-slate-200">{String(s.engine_enabled)}</span></span>
                  <span>disable_reason: <span className="text-slate-200">{s.engine_disable_reason || "—"}</span></span>
                  <span>selection: <span className="text-slate-200">
                    {Object.entries(s.selection_permissions).filter(([, v]) => v).map(([k]) => k).join(", ") || "none"}
                  </span></span>
                </div>

                <AffList title="Primary cells" items={(s.diagnostic_affinities.primary_cells || []).map((c) => ({
                  head: `${c.regulation} × ${c.field}`, ...c }))} />
                <AffList title="Secondary field affinities" items={(s.diagnostic_affinities.secondary_field_affinities || []).map((a) => ({ head: a.field, ...a }))} />
                <AffList title="Friction affinities" items={(s.diagnostic_affinities.friction_affinities || []).map((a) => ({ head: a.field || a.dimension, ...a }))} />
                <AffList title="Integration affinities" items={(s.integration_affinities || []).map((a) => ({ head: a.dimension, ...a }))} />
                <AffList title="Support domains" items={(s.support_domains || []).map((a) => ({ head: a.domain, ...a }))} />
                <AffList title="Calibration hypotheses" items={(s.calibration_hypotheses || []).map((h) => ({
                  head: `${h.hypothesis_type} → ${Object.values(h.proposed_mapping || {}).join(" × ")}`, ...h }))} />

                {s.functional_tags?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-1">Functional tags</p>
                    <div className="flex flex-wrap gap-1">
                      {s.functional_tags.map((t) => (
                        <span key={t} className="text-[11px] rounded-full bg-black/40 border border-white/10 px-2 py-0.5 text-slate-300 font-mono">{t}</span>
                      ))}
                    </div>
                  </div>
                )}
                {s.exclusion_notes?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-amber-300/70 mb-1">Exclusion notes</p>
                    <ul className="list-disc list-inside text-[12px] text-slate-400 space-y-0.5">
                      {s.exclusion_notes.map((n, i) => <li key={i}>{n}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/10 p-10 text-center text-slate-500">No stones match.</div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
      <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60">{label}</p>
      <p className="mt-1 text-sm font-mono text-slate-100" data-testid={testId}>{value}</p>
    </div>
  );
}

function AffList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-fuchsia-300/60 mb-1">{title}</p>
      <div className="space-y-1">
        {items.map((a, i) => (
          <div key={i} className="text-[12px] font-mono border-b border-white/5 py-1 last:border-0">
            <span className="text-slate-100">{a.head}</span>
            {a.status && <span className="text-slate-500"> · {a.status}</span>}
            {a.origin && <span className="text-slate-500"> · {a.origin}</span>}
            <span className={a.scoring_eligible ? "text-emerald-300" : "text-slate-500"}>
              {" "}· scoring-eligible {String(!!a.scoring_eligible)}
            </span>
            {a.threshold_eligible !== undefined && (
              <span className="text-slate-500"> · threshold {String(a.threshold_eligible)}</span>
            )}
            {a.rationale && <p className="text-slate-500 not-italic mt-0.5 pl-2">{a.rationale}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
